import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Card,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import PlaceIcon from '@mui/icons-material/Place'
import EventIcon from '@mui/icons-material/Event'
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle'
import BuildIcon from '@mui/icons-material/Build'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import API from '../../../../services/API'
import { VehicleContext } from '../SingleVehicle'
import { SectionHeader } from './SectionKit'

const TYPE_META = {
  event: { label: 'Event', icon: EventIcon, color: 'primary' },
  pts: { label: 'PTS', icon: AirportShuttleIcon, color: 'secondary' },
  service: { label: 'Service', icon: BuildIcon, color: 'warning' },
  mot: { label: 'MOT', icon: FactCheckIcon, color: 'warning' },
  downtime: { label: 'Downtime', icon: EventBusyIcon, color: 'warning' },
}

const statusColor = (status) => {
  if (!status) return 'default'
  if (['Pass', 'Confirmed', 'Closed', 'Completed'].includes(status))
    return 'success'
  if (['Fail', 'Cancelled'].includes(status)) return 'error'
  return 'default'
}

// Services/MOTs are date-only; events and duties have a start-end window.
const formatWhen = (item) => {
  if (!item.end) return moment(item.start).format('ddd D MMM YYYY')
  const start = moment(item.start)
  const end = moment(item.end)
  if (start.isSame(end, 'day')) {
    return `${start.format('ddd D MMM YYYY, HH:mm')} – ${end.format('HH:mm')}`
  }
  return `${start.format('D MMM YYYY HH:mm')} – ${end.format('D MMM YYYY HH:mm')}`
}

const linkFor = (item) => {
  if (item.type === 'event') return `/events/${item.eventId}`
  if (item.type === 'pts') {
    return `/pts/planning?date=${moment(item.start).format('YYYYMMDD')}&duty=${item.dutyId}`
  }
  return null
}

const DeploymentRow = ({ item, onOpenServicing }) => {
  const navigate = useNavigate()
  const meta = TYPE_META[item.type] || TYPE_META.event
  const Icon = meta.icon
  const link = linkFor(item)
  const inProgress =
    item.end &&
    moment().isBetween(moment(item.start), moment(item.end), null, '[]')
  const onClick = link
    ? () => navigate(link)
    : item.type === 'service' || item.type === 'mot'
      ? onOpenServicing
      : undefined

  return (
    <Stack
      direction="row"
      spacing={1.5}
      py={1.25}
      alignItems="flex-start"
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        borderRadius: 1.5,
        px: 1,
        mx: -1,
        '&:hover': onClick ? { bgcolor: 'action.hover' } : undefined,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          bgcolor: `${meta.color}.main`,
          color: `${meta.color}.contrastText`,
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Box minWidth={0} flex={1}>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="body2" fontWeight={700} sx={{ minWidth: 0 }}>
            {item.title}
          </Typography>
          <Chip
            size="small"
            label={meta.label}
            variant="outlined"
            sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem' }}
          />
          {inProgress && (
            <Chip
              size="small"
              label="In progress"
              color="success"
              sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem' }}
            />
          )}
          {item.status && (
            <Chip
              size="small"
              label={item.status}
              color={statusColor(item.status)}
              sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" component="div">
          {formatWhen(item)}
          {item.type === 'pts' &&
            ` · ${item.journeyCount || 0} journey${item.journeyCount === 1 ? '' : 's'}`}
        </Typography>
        {item.location && (
          <Stack direction="row" gap={0.5} alignItems="center">
            <PlaceIcon fontSize="inherit" color="action" />
            <Typography variant="caption" color="text.secondary" noWrap>
              {item.location}
            </Typography>
          </Stack>
        )}
        {item.detail && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ overflowWrap: 'anywhere' }}
          >
            {item.detail}
          </Typography>
        )}
      </Box>
    </Stack>
  )
}

const DeploymentList = ({ title, caption, items, emptyText, onOpenServicing }) => (
  <Card variant="outlined" sx={{ p: 2, borderRadius: 2.5, flex: 1}}>
    <SectionHeader title={title} caption={caption} />
    {items.length ? (
      <Stack divider={<Divider sx={{ opacity: 0.6 }} />} mt={1}>
        {items.map((item) => (
          <DeploymentRow
            key={`${item.type}-${item._id}`}
            item={item}
            onOpenServicing={onOpenServicing}
          />
        ))}
      </Stack>
    ) : (
      <Typography variant="body2" color="text.disabled" mt={1}>
        {emptyText}
      </Typography>
    )}
  </Card>
)

// Last 6 / next 6 commitments for the vehicle across events, PTS duties and
// workshop visits (services + MOTs), merged server-side.
const DeploymentsSection = ({ onOpenServicing }) => {
  const { vehicle } = useContext(VehicleContext)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [data, setData] = useState({ past: [], upcoming: [] })

  useEffect(() => {
    if (!vehicle?._id) return
    setLoading(true)
    setError(false)
    API.get(`/vehicle/${vehicle._id}/deployments`)
      .then((res) => {
        if (res.status === 200) {
          setData({
            past: res.data?.past || [],
            upcoming: res.data?.upcoming || [],
          })
        } else {
          setError(true)
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [vehicle?._id])

  if (loading) return <LinearProgress />
  if (error) {
    return (
      <Alert severity="error">
        Unable to load deployments for this vehicle. Please try again later.
      </Alert>
    )
  }

  return (
    <Box>
    <Grid container spacing={2}>
      <Grid item xs={12} md={6} display='flex'>
        <DeploymentList
          title="Upcoming"
          caption="Next 6 events, PTS duties, services and MOTs"
          items={data.upcoming}
          emptyText="Nothing scheduled for this vehicle."
          onOpenServicing={onOpenServicing}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <DeploymentList
          title="Recent"
          caption="Last 6 events, PTS duties, services and MOTs"
          items={data.past}
          emptyText="No previous deployments recorded for this vehicle."
          onOpenServicing={onOpenServicing}
        />
      </Grid>
    </Grid>
    </Box>
  )
}

export default DeploymentsSection
